import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { configurePlusAppsync } from 'src/context/AuthContextUtility';
import { listPreference, createPreference, updatePreference } from 'src/utilities/AppSyncPlusAPIs';
import { createAuditLog } from '../Utils/AppActivityUtils';

import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Select,
  Container,
  Alert,
  StatusIndicator,
  Spinner
} from "@amzn/awsui-components-react";
import './style.scss';
import { v4 as uuidv4 } from 'uuid';
import EyeOpenIcon from '../../../images/eye-open.svg';
import EyeClosedIcon from '../../../images/eye-closed.svg';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DEFAULT_SCHEDULER_TAB, DEFAULT_TABS } from '../Constants';

const SortableTabItem = ({ tab, index, onToggleVisibility, isAnyDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`draggable-tab-item ${!tab.visible ? 'tab-hidden' : ''}`}
    >
      <Box>
        <span className="drag-handle" {...attributes} {...listeners} style={{ cursor: 'grab' }}>☰</span>
      </Box>
      <Box className="tab-label">
        <strong style={{ opacity: tab.visible ? 1 : 0.5 }}>{tab.label}</strong>
      </Box>
      <Box color="text-body-secondary">
        {!isAnyDragging && `Order: ${index + 1}`}
      </Box>
      <Box>
        <img
          src={tab.visible ? EyeOpenIcon : EyeClosedIcon}
          alt={tab.visible ? "Hide tab" : "Show tab"}
          className="eye-icon"
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(tab.id);
          }}
        />
      </Box>
    </div>
  );
};

const ActionTabsSettingsModal = ({
  visible,
  onDismiss,
  onConfigUpdated,
  appName,
  appId
}) => {
  const [errorText, setErrorText] = useState("");
  const [successText, setSuccessText] = useState("");
  const [loading, setLoading] = useState(false);
  const userId = useSelector((globalState) => globalState.auth.userId);

  // Configuration state - tabs loaded from user preference system
  const [tabsConfig, setTabsConfig] = useState({
    actionTabs: DEFAULT_TABS.map(tab => ({ ...tab })),
    customActionTabs: []
  });

  // Form states for update operation
  const [selectedTabToUpdate, setSelectedTabToUpdate] = useState(null);
  const [newTabName, setNewTabName] = useState("");

  const [activeId, setActiveId] = useState(null);

  const [fullPreferenceObject, setFullPreferenceObject] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load tabs when modal opens
  useEffect(() => {
    if (visible && userId && appId) {
      setLoading(true);
      loadActionTabsConfiguration();
    }
  }, [visible, userId, appId]);

  useEffect(() => {
    if (!visible) {
      setErrorText("");
      setSuccessText("");
      setSelectedTabToUpdate(null);
      setNewTabName("");
      setLoading(false);
    }
  }, [visible]);

  const loadActionTabsConfiguration = () => {
    setLoading(true);
    setErrorText("");

    configurePlusAppsync()
      .then(() => {
        loadUserTabPreference();
      })
      .catch((error) => {
        console.error('Error loading tab configuration:', error);
        setErrorText('Failed to load tab configuration');
        setLoading(false);
      });
  };

  const loadUserTabPreference = () => {
    listPreference(
      appId,
      'APP',
      'TAB_CONFIGURATION',
      function onSuccess(response) {
        const preferences = response?.data?.listPreference?.items || [];
        
        const validPreferences = preferences.filter(pref => {
          if (pref.information) {
            const information = typeof pref.information === 'string'
              ? JSON.parse(pref.information)
              : pref.information;
            return information && information.tabConfiguration;
          }
          return false;
        });

        // Use the first preference (backend always updates the first record)
        if (validPreferences.length > 0) {
          const firstPreference = validPreferences[0];
          
          // Store the complete preference object to preserve all fields
          setFullPreferenceObject(firstPreference);

          const information = typeof firstPreference.information === 'string'
            ? JSON.parse(firstPreference.information)
            : firstPreference.information;

          if (information && information.tabConfiguration) {
            // needed for custom configs created before cruzer existed
            const existingTabs = Array.isArray(information.tabConfiguration.actionTabs)
              ? information.tabConfiguration.actionTabs
              : [];
            if (!existingTabs.some(tab => tab.id === DEFAULT_SCHEDULER_TAB.id)) {
              existingTabs.push({ ...DEFAULT_SCHEDULER_TAB });
            }
            information.tabConfiguration.actionTabs = existingTabs;
            setTabsConfig(information.tabConfiguration);
          }
        } else {
          setFullPreferenceObject(null);
        }
        
        setLoading(false);
      },
      function onFailure(error) {
        console.error('Error loading preferences:', error);
        setFullPreferenceObject(null);
        setLoading(false);
      }
    );
  };

  const handleUpdateTab =  () => {
    setErrorText("");
    setLoading(true);

    if (!selectedTabToUpdate) {
      setErrorText("Please select a Tab to update");
      setLoading(false);
      return;
    }

    if (!newTabName.trim()) {
      setErrorText("Please provide a new tab name");
      setLoading(false);
      return;
    }

    try {
      // Find the selected tab
      const actionTabsArray = Array.isArray(tabsConfig?.actionTabs) ? tabsConfig.actionTabs : [];
      const customTabsArray = Array.isArray(tabsConfig?.customActionTabs) ? tabsConfig.customActionTabs : [];

      const selectedTab = [...actionTabsArray, ...customTabsArray]
        .find(tab => tab.id === selectedTabToUpdate.value);

      if (!selectedTab) {
        throw new Error("Selected tab not found");
      }
 const beforeState = JSON.parse(JSON.stringify(tabsConfig));

      // Update local state
      const updatedConfig = { ...tabsConfig };
      const actionTabIndex = actionTabsArray.findIndex(tab => tab.id === selectedTabToUpdate.value);
      const customTabIndex = customTabsArray.findIndex(tab => tab.id === selectedTabToUpdate.value);

      if (actionTabIndex !== -1) {
        updatedConfig.actionTabs[actionTabIndex] = {
          ...updatedConfig.actionTabs[actionTabIndex],
          label: newTabName.trim()
        };
      } else if (customTabIndex !== -1) {
        updatedConfig.customActionTabs[customTabIndex] = {
          ...updatedConfig.customActionTabs[customTabIndex],
          label: newTabName.trim()
        };
      }

   
      saveUserTabPreference(updatedConfig, beforeState);


    } catch (error) {
      console.error('Error updating tab:', error);
      const errorMessage = error?.errors?.[0]?.message || error?.message || "Failed to Update Tab";
      setErrorText(errorMessage);


    }
  };

  // Save user tab preference (create or update)
  const saveUserTabPreference = (config, beforeStateParam = null) => {

    const label = `Tab Configuration for ${appId}`;
    const information = {
      userId: userId,
      appId: appId,
      tabConfiguration: config
    };

  const beforeState = beforeStateParam || tabsConfig;

    if (fullPreferenceObject?.preferenceId) {
      const updatedPreference = {
        ...fullPreferenceObject,
        label: label,
        lastUpdatedBy: userId,
        lastUpdatedTimestamp: Date.now().toString(),
        information: information
      };

      updatePreference(
        fullPreferenceObject.preferenceId,
        label,
        userId,
        information,
        function onSuccess(updateResponse) {
          setFullPreferenceObject(updatedPreference);
          setTabsConfig(config);
          setSuccessText("Tab order saved successfully!");
          setSelectedTabToUpdate(null);
          setNewTabName("");
          setLoading(false);
       try {
            const timestamp = Date.now();
            const pk = `APP#${appId}`; 
            const sk = `${timestamp}`; 
            const action = "SAVE_TAB_CONFIGURATION";
            const actor = userId;
            const comments = `User saved action tab configuration for app ${appId}`;
       
            const beforeData = beforeState || {};
            const afterData = config;
            
            createAuditLog(
              pk,
              sk,
              comments,
              JSON.stringify(beforeData),
              JSON.stringify(afterData),
              action,
              actor,
           
            );
          } catch (auditError) {
            console.error('Error creating audit log:', auditError);
          }
          // Notify parent component of configuration update
          if (onConfigUpdated) {
            onConfigUpdated();
          }
        },
        function onFailure(error) {
          console.error('Error saving tab order:', error);
          const errorMessage = error?.errors?.[0]?.message || error?.message || "Failed to save tab order";
          setErrorText(errorMessage);
          setLoading(false);
        }
      );
    } else {
      const newPreferenceId = uuidv4();
      createPreference(
        newPreferenceId,
        'TAB_CONFIGURATION',
        label,
        appId,
        'APP',
        userId,
        information,
        null, // ttl
        function onSuccess(updateResponse) {
          // Update the full preference object with the newly created preference
          const newPreference = {
            preferenceId: newPreferenceId,
            label: label,
            associationId: appId,
            associationIdType: 'APP',
            preferenceType: 'TAB_CONFIGURATION',
            createdBy: userId,
            lastUpdatedBy: userId,
            lastUpdatedTimestamp: Date.now().toString(),
            information: information
          };
          setFullPreferenceObject(newPreference);
          setTabsConfig(config);
          setSuccessText("Tab order saved successfully!");
          setSelectedTabToUpdate(null);
          setNewTabName("");
          setLoading(false);
       try {
            const timestamp = Date.now();
            const pk = `APP#${appId}`; // App ID with APP prefix to match audit log tab query
            const sk = `${timestamp}`; // Timestamp as sort key
            const action = "SAVE_TAB_CONFIGURATION";
            const actor = userId;
            const comments = `User saved action tab configuration for app ${appId}`;
      const beforeData = beforeState || {};
            const afterData = config;
            
            createAuditLog(
              pk,
              sk,
              comments,
              JSON.stringify(beforeData),
              JSON.stringify(afterData),
              action,
              actor,
           
            );
          } catch (auditError) {
          console.error('Error creating audit log:', auditError);
          }
          // Notify parent component of configuration update
          if (onConfigUpdated) {
            onConfigUpdated();
          }
        },
        function onFailure(error) {
          console.error('Error saving tab order:', error);
          const errorMessage = error?.errors?.[0]?.message || error?.message || "Failed to save tab order";
          setErrorText(errorMessage);
          setLoading(false);
        }
      );
    }

  };

  // Save tab order to database
  const saveTabOrder = (reorderedTabs) => {
    setLoading(true);
    setErrorText("");

    const updatedConfig = {
      actionTabs: reorderedTabs,
      customActionTabs: tabsConfig.customActionTabs || []
    };

    saveUserTabPreference(updatedConfig);

  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const actionTabsArray = Array.isArray(tabsConfig?.actionTabs) ? tabsConfig.actionTabs : [];
    const validTabs = actionTabsArray.filter(tab => tab && tab.id && tab.label);

    const oldIndex = validTabs.findIndex(tab => tab.id === active.id);
    const newIndex = validTabs.findIndex(tab => tab.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedTabsList = arrayMove(validTabs, oldIndex, newIndex);

    const reorderedTabs = reorderedTabsList.map((tab, index) => ({
      ...tab,
      order: index + 1
    }));

    // Update local state immediately for better UX
    setTabsConfig({
      ...tabsConfig,
      actionTabs: reorderedTabs
    });

    saveTabOrder(reorderedTabs);
  };

  const handleToggleVisibility = (tabId) => {
    setLoading(true);
    setErrorText("");
    setSuccessText("");

    const actionTabsArray = Array.isArray(tabsConfig?.actionTabs) ? tabsConfig.actionTabs : [];
    
    const updatedTabs = actionTabsArray.map(tab => {
      if (tab.id === tabId) {
        return {
          ...tab,
          visible: !tab.visible
        };
      }
      return tab;
    });

    const beforeState = JSON.parse(JSON.stringify(tabsConfig));

    const updatedConfig = {
      ...tabsConfig,
      actionTabs: updatedTabs
    };

    setTabsConfig(updatedConfig);

    saveUserTabPreference(updatedConfig, beforeState);
  };

  const renderReorderTabSection = () => {
    const actionTabsArray = Array.isArray(tabsConfig?.actionTabs) ? tabsConfig.actionTabs : [];
    const validTabs = actionTabsArray.filter(tab => tab && tab.id && tab.label);
    const activeTab = activeId ? validTabs.find(tab => tab.id === activeId) : null;

    return (
      <SpaceBetween direction="vertical" size="m">
        <Box variant="h3" color="text-label">Reorder, Hide/Unhide Tabs</Box>
        <Container>
          {loading ? (
            <Box padding="m" textAlign="center">
              <Spinner size="normal" /> Loading tabs...
            </Box>
          ) : (
            <SpaceBetween direction="vertical" size="xs">
              <Box variant="p" color="text-body-secondary">
                Drag and Drop Tabs to Reorder
              </Box>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveId(null)}
              >
                <SortableContext
                  items={validTabs.map(tab => tab.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {validTabs.map((tab, index) => (
                    <SortableTabItem
                      key={tab.id}
                      tab={tab}
                      index={index}
                      onToggleVisibility={handleToggleVisibility}
                      isAnyDragging={!!activeId}
                    />
                  ))}
                </SortableContext>
                <DragOverlay>
                  {activeTab ? (
                    <div className={`draggable-tab-item ${!activeTab.visible ? 'tab-hidden' : ''}`} style={{ opacity: 0.8 }}>
                      <Box>
                        <span className="drag-handle">☰</span>
                      </Box>
                      <Box className="tab-label">
                        <strong style={{ opacity: activeTab.visible ? 1 : 0.5 }}>{activeTab.label}</strong>
                      </Box>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </SpaceBetween>
          )}
        </Container>
      </SpaceBetween>
    );
  };

  const renderUpdateTabSection = () => {
    // Get all available tabs for the dropdown
    const actionTabsArray = Array.isArray(tabsConfig?.actionTabs) ? tabsConfig.actionTabs : [];
    const customTabsArray = Array.isArray(tabsConfig?.customActionTabs) ? tabsConfig.customActionTabs : [];

    const allTabs = [
      ...actionTabsArray.filter(tab => tab && tab.id).map(tab => ({
        label: tab?.label || tab?.id || 'Tab',
        value: tab.id
      })),
      ...customTabsArray.filter(tab => tab && tab.id).map(tab => ({
        label: `${tab?.label || tab?.id || 'Custom Tab'} (Custom)`,
        value: tab.id
      }))
    ];

    return (
      <SpaceBetween direction="vertical" size="m">
        <Box variant="h3" color="text-label">Rename Tabs</Box>
        <Container>
          {loading ? (
            <Box padding="m" textAlign="center">
              <Spinner size="normal" /> Loading tabs...
            </Box>
          ) : (
            <SpaceBetween direction="vertical" size="m">
              <FormField label="Tabs to Update">
                <Select
                  selectedOption={selectedTabToUpdate}
                  onChange={({ detail }) => {
                    setSelectedTabToUpdate(detail.selectedOption);
                    // Pre-fill the new name with current name
                    const actionTabsArray = Array.isArray(tabsConfig?.actionTabs) ? tabsConfig.actionTabs : [];
                    const customTabsArray = Array.isArray(tabsConfig?.customActionTabs) ? tabsConfig.customActionTabs : [];
                    const selectedTab = [...actionTabsArray, ...customTabsArray]
                      .find(tab => tab.id === detail.selectedOption.value);
                    setNewTabName(selectedTab ? selectedTab.label : "");
                  }}
                  options={allTabs}
                  placeholder="Select a Tabs to Update"
                  selectedAriaLabel="Selected"
                />
              </FormField>

              <FormField label="New Tab Name">
                <Input
                  value={newTabName}
                  onChange={({ detail }) => setNewTabName(detail.value)}
                  placeholder="Provide a Tab Name"
                  disabled={!selectedTabToUpdate}
                />
              </FormField>
            </SpaceBetween>
          )}
        </Container>

        {errorText && (
          <Box>
            <StatusIndicator type="error">{errorText}</StatusIndicator>
          </Box>
        )}
        {successText && (
          <Box>
            <StatusIndicator type="success">{successText}</StatusIndicator>
          </Box>
        )}

        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={onDismiss}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateTab}
              disabled={!selectedTabToUpdate || !newTabName.trim()}
              loading={loading}
            >
              Update Tab
            </Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    );
  };


  return (
    <Modal
      onDismiss={onDismiss}
      visible={visible}
      size="large"
      header={`Customize Action Tabs - ${appName || 'Application'}`}
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert type="info">
          <Box>
            Through this interface, ateam-bi can customize application tabs. Changes will be visible to all users of this application.
          </Box>
        </Alert>

        {renderReorderTabSection()}

        {renderUpdateTabSection()}
      </SpaceBetween>
    </Modal>
  );
};

export default ActionTabsSettingsModal;
